import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { CalendarDays, Users, Search, Loader2 } from "lucide-react";

const today = () => new Date().toISOString().split("T")[0];
const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};

export default function SearchBar({ variant = "hero" }) {
  const [searchParams] = useSearchParams();
  const [checkIn, setCheckIn] = useState(searchParams.get("check_in") || plusDays(3));
  const [checkOut, setCheckOut] = useState(searchParams.get("check_out") || plusDays(5));
  const [guests, setGuests] = useState(Number(searchParams.get("guests") || 2));
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => { setSubmitting(false); }, [location.pathname, location.search]);

  // Calendar contract: the checkout date can never be on or before the
  // check-in date. When the user changes check-in, we auto-snap check-out
  // to (check-in + 1 day) so submitting an invalid range is impossible.
  // The check-out picker also disables earlier dates via `min`.
  const addDays = (iso, n) => {
    const d = new Date(iso);
    d.setDate(d.getDate() + n);
    return d.toISOString().split("T")[0];
  };
  const onCheckInChange = (value) => {
    setCheckIn(value);
    if (value && (!checkOut || new Date(checkOut) <= new Date(value))) {
      setCheckOut(addDays(value, 1));
    }
  };
  const checkOutMin = checkIn ? addDays(checkIn, 1) : today();

  const submit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    const params = new URLSearchParams();
    if (checkIn) params.set("check_in", checkIn);
    if (checkOut) params.set("check_out", checkOut);
    params.set("guests", String(guests));
    // Preserve an existing location filter on the URL so switching dates doesn't wipe it.
    const loc = searchParams.get("location");
    if (loc) params.set("location", loc);
    setTimeout(() => nav(`/stays?${params.toString()}`), 150);
    setTimeout(() => setSubmitting(false), 1200);
  };

  const isHero = variant === "hero";

  return (
    <form
      onSubmit={submit}
      data-testid="search-bar"
      className={`w-full ${isHero ? "bg-parchment/95" : "bg-white"} rounded-2xl border border-forest/15 p-2 shadow-card backdrop-blur sm:p-3`}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="grid grid-cols-2 gap-1 sm:contents">
          {/* Check in */}
          <div className="flex items-center gap-1.5 rounded-xl px-2.5 py-2 hover:bg-mist/60 transition-colors sm:flex-1 sm:gap-2 sm:px-4 sm:py-3">
            <CalendarDays size={16} className="shrink-0 text-forest" />
            <div className="min-w-0 flex-1">
              <label className="block text-[10px] uppercase tracking-wider text-amber sm:text-[11px]">Check in</label>
              <input
                type="date"
                value={checkIn}
                min={today()}
                onChange={(e) => onCheckInChange(e.target.value)}
                className="w-full bg-transparent text-[13px] font-medium text-noir focus:outline-none sm:text-sm"
                data-testid="search-checkin"
              />
            </div>
          </div>
          <div className="hidden sm:block h-10 w-px bg-forest/10" />

          {/* Check out */}
          <div className="flex items-center gap-1.5 rounded-xl px-2.5 py-2 hover:bg-mist/60 transition-colors sm:flex-1 sm:gap-2 sm:px-4 sm:py-3">
            <CalendarDays size={16} className="shrink-0 text-forest" />
            <div className="min-w-0 flex-1">
              <label className="block text-[10px] uppercase tracking-wider text-amber sm:text-[11px]">Check out</label>
              <input
                type="date"
                value={checkOut}
                min={checkOutMin}
                onChange={(e) => setCheckOut(e.target.value)}
                className="w-full bg-transparent text-[13px] font-medium text-noir focus:outline-none sm:text-sm"
                data-testid="search-checkout"
              />
            </div>
          </div>
          <div className="hidden sm:block h-10 w-px bg-forest/10" />

          {/* Guests */}
          <div className="col-span-2 flex items-center gap-1.5 rounded-xl px-2.5 py-2 hover:bg-mist/60 transition-colors sm:flex-1 sm:gap-2 sm:px-4 sm:py-3">
            <Users size={16} className="shrink-0 text-forest" />
            <div className="min-w-0 flex-1">
              <label className="block text-[10px] uppercase tracking-wider text-amber sm:text-[11px]">Guests</label>
              <select
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                className="w-full bg-transparent text-[13px] font-medium text-noir focus:outline-none sm:text-sm"
                data-testid="search-guests"
              >
                {Array.from({ length: 16 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? "guest" : "guests"}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <button type="submit" disabled={submitting} className="vyg-cta w-full justify-center sm:!px-6 sm:w-auto disabled:opacity-80" data-testid="search-submit">
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          {submitting ? "Searching…" : "Check availability"}
        </button>
      </div>
      {submitting && (
        <div data-testid="search-progress" className="mt-3 flex items-center gap-2 rounded-xl bg-gold/10 px-3 py-2 text-xs text-forest">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
          </span>
          <span>Talking to our calendar — fetching live availability &amp; rates.</span>
          <span className="ml-auto h-0.5 flex-1 overflow-hidden rounded-full bg-forest/10">
            <span className="block h-full w-1/3 animate-shimmer bg-gradient-to-r from-transparent via-gold to-transparent" />
          </span>
        </div>
      )}
    </form>
  );
}
